/**
 * Thank You Page Sharing Composable
 *
 * Handles URL parameter parsing, NBC data fetching, and share URL construction
 * for the thank you page after successful booking.
 *
 * @example
 * ```vue
 * <script setup lang="ts">
 * import { useThankYouSharing } from '@/composables/checkout/useThankYouSharing';
 *
 * const { params, shareUrl, isLoading, canShare, initialize } = useThankYouSharing();
 *
 * onMounted(async () => {
 *   await initialize();
 * });
 * </script>
 *
 * <template>
 *   <a v-if="canShare" :href="facebookShareUrl">Share on Facebook</a>
 * </template>
 * ```
 */

import { ref, computed } from 'vue';
import { getProduct } from '@/services/api/nbc/getProduct';
import type { NBCSingleProductResponse } from '@/types/schemas/nbc';

export interface ThankYouParams {
  bookingNumber: string | null;
  productCode: string | null;
  checkIn: string | null;
  checkOut: string | null;
  nights: string | null;
  pax: string | null;
}

export function useThankYouSharing() {
  const params = ref<ThankYouParams>({
    bookingNumber: null,
    productCode: null,
    checkIn: null,
    checkOut: null,
    nights: null,
    pax: null,
  });

  const nbcData = ref<NBCSingleProductResponse | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  /**
   * Parse URL parameters from current page URL
   */
  function parseUrlParams(): void {
    const urlParams = new URLSearchParams(window.location.search);
    params.value = {
      bookingNumber: urlParams.get('bookingNumber'),
      productCode: urlParams.get('productCode'),
      checkIn: urlParams.get('checkIn'),
      checkOut: urlParams.get('checkOut'),
      nights: urlParams.get('nights'),
      pax: urlParams.get('pax'),
    };
  }

  /**
   * Fetch NBC product data using productCode from URL params
   */
  async function fetchNbcData(): Promise<void> {
    if (!params.value.productCode) {
      return;
    }

    isLoading.value = true;
    error.value = null;

    try {
      nbcData.value = await getProduct(params.value.productCode);
    } catch (e) {
      console.warn('[ThankYouSharing] Failed to fetch NBC data:', e);
      error.value = e instanceof Error ? e.message : 'Failed to load product data';
      nbcData.value = null;
      // Don't throw - graceful degradation
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Build share URL from PDP URL + booking parameters
   */
  const shareUrl = computed((): string | null => {
    if (!nbcData.value?.pdpUrl) return null;

    try {
      // Handle both absolute and relative URLs
      const baseUrl = nbcData.value.pdpUrl.startsWith('http')
        ? nbcData.value.pdpUrl
        : `${window.location.origin}${nbcData.value.pdpUrl}`;

      const url = new URL(baseUrl);

      if (params.value.checkIn) {
        url.searchParams.set('date', params.value.checkIn);
      }
      if (params.value.checkOut) {
        url.searchParams.set('todate', params.value.checkOut);
      }
      if (params.value.nights) {
        url.searchParams.set('days', params.value.nights);
      }
      if (params.value.pax) {
        url.searchParams.set('pax', params.value.pax);
      }

      return url.toString();
    } catch (e) {
      console.warn('[ThankYouSharing] Failed to build share URL:', e);
      return null;
    }
  });

  const hasShareUrl = computed(() => shareUrl.value !== null);
  const canShare = computed(() => hasShareUrl.value && !isLoading.value);
  const hasNbcData = computed(() => nbcData.value !== null);

  /**
   * Initialize composable - parse params and fetch NBC data
   */
  async function initialize(): Promise<void> {
    parseUrlParams();
    if (params.value.productCode) {
      await fetchNbcData();
    }
  }

  return {
    // State
    params,
    nbcData,
    isLoading,
    error,

    // Computed
    shareUrl,
    hasShareUrl,
    hasNbcData,
    canShare,

    // Actions
    initialize,
    parseUrlParams,
    fetchNbcData,
  };
}
